import { create } from 'zustand'
import { toast } from 'sonner'
import { signaturesApi } from '@/lib/api'
import type { EmailSignature } from '@/types/signature'

/**
 * Settings > Signatures, the launch wizard's signature step, and the
 * Mailboxes screen (on a plan with per_user_signatures) all read the
 * current user's own signature from here. `planLocked` records a 403 from
 * the plan gate (Starter has no per-user signature) so the UI shows the
 * upsell card instead of an error toast.
 */
interface SignatureState {
  mine: EmailSignature | null
  loading: boolean
  loaded: boolean
  planLocked: boolean

  fetchMine: () => Promise<void>
  saveMine: (html: string) => Promise<EmailSignature>
  deleteMine: () => Promise<void>
  reset: () => void
}

const initialState = {
  mine: null as EmailSignature | null,
  loading: false,
  loaded: false,
  planLocked: false,
}

function isPlanGate(error: any): boolean {
  return error?.response?.status === 403 && /plan/i.test(String(error?.response?.data?.detail ?? ''))
}

export const useSignatureStore = create<SignatureState>()((set) => ({
  ...initialState,

  fetchMine: async () => {
    set({ loading: true })
    try {
      const res = await signaturesApi.getMine()
      set({ mine: res.data, loading: false, loaded: true, planLocked: false })
    } catch (error: any) {
      set({ loading: false, loaded: true, planLocked: isPlanGate(error) })
    }
  },

  saveMine: async (html) => {
    try {
      const res = await signaturesApi.saveMine(html)
      set({ mine: res.data })
      toast.success('Signature saved')
      return res.data
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save signature')
      throw error
    }
  },

  deleteMine: async () => {
    try {
      await signaturesApi.deleteMine()
      set({ mine: null })
      toast.success('Signature deleted')
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to delete signature')
      throw error
    }
  },

  reset: () => set(initialState),
}))
